import { gql } from '@apollo/client';
import { Badge, Box, Image, Text, VStack } from '@chakra-ui/react';
import { DocumentNode } from 'graphql';
import * as React from 'react';

import { AbbreviateRedraft } from '@/components/AbbreviateRedraft';
import { Card } from '@/imports/ui/chakra/components/Card';
import { ImageFallback } from '@/imports/ui/chakra/components/ImageFallback';

import { BlogDetail_BlogFragment } from './graphql-types/BlogDetail_BlogFragment';

import { Comments } from './components/Comments';
import {
  CreateComment,
  CreateComment_postFragment,
} from './components/CreateComment';
import { DeletedHeader } from './components/DeletedHeader';
import { LikeCommentFooter } from './components/LikeCommentFooter';
import { Moderation } from './components/Moderation';
import { ModerationHeader } from './components/ModerationHeader';
import { PinnedHeader } from './components/PinnedHeader';
import { PostHeader } from './components/PostHeader';
import { Tags } from './components/Tags';
import { useCommentActions } from './hooks/useCommentActions';
import { useModeration } from './hooks/useModeration';

export const BlogDetail: React.FC<
  { blog: BlogDetail_BlogFragment } & {
    options?: { isExpandedByDefault?: boolean };
  }
> & {
  blogFragment: DocumentNode;
} = ({ blog, options }) => {
  const { shouldHidePost, shouldShowHeader } = useModeration({
    post: blog,
    isAnswerHeader: false,
  });
  const {
    ref,
    focusAndScrollToRef,
    expandOrFocusAndScrollToRef,
    isActive,
    setIsActive,
    ...expandProps
  } = useCommentActions({
    post: blog,
    isExpandedByDefault: options?.isExpandedByDefault,
  });

  return shouldHidePost ? (
    <Moderation post={blog} />
  ) : (
    <Card>
      {blog.deleted && <DeletedHeader post={blog} isAnswerHeader={false} />}
      {shouldShowHeader && (
        <ModerationHeader post={blog} isAnswerHeader={false} />
      )}
      <PinnedHeader post={blog} />
      {blog.bannerImage?.imageProxy && (
        <Image
          w="full"
          alt=""
          borderTopRadius={shouldShowHeader || blog.pinned ? '' : 'inherit'}
          src={blog.bannerImage?.imageProxy || undefined}
          fallback={<ImageFallback aspectRatio="8/3" />}
        />
      )}
      <Box p="4">
        <PostHeader post={blog} mb="4" />
        {!blog.published && (
          <Badge variant="solid" colorScheme="gray" mb="2">
            DRAFT
          </Badge>
        )}
        {blog.blogTitle && (
          <Text
            mb="2"
            fontSize="30px"
            lineHeight="36px"
            fontWeight="600"
            color="gray.700"
          >
            {blog.blogTitle}
          </Text>
        )}
        {blog.content && (
          <AbbreviateRedraft
            rawDraftContentState={blog.content}
            noOfCharacters={500}
          />
        )}
        <VStack spacing="4" width="full" align="start" mt="4">
          <Tags post={blog} />
          <LikeCommentFooter
            post={blog}
            onClickComment={focusAndScrollToRef}
            onClickCommentCount={expandOrFocusAndScrollToRef}
          />
          <Comments post={blog} expandProps={expandProps} />
          <CreateComment
            post={blog}
            ref={ref}
            isActive={isActive}
            setIsActive={setIsActive}
          />
        </VStack>
      </Box>
    </Card>
  );
};

BlogDetail.blogFragment = gql`
  fragment BlogDetail_BlogFragment on BlogV2 {
    ...PostHeader_PostFragment
    ...useModeration_PostFragment
    _id
    deleted
    published
    blogTitle: title
    content
    bannerImage {
      _id
      imageProxy(input: { ar: "8:3", w: 1600, fit: "fill", fill: "blur" })
    }
    pinned
    ...Tags_PostFragment
    ...LikeCommentFooter_PostFragment
    ...ModerationHeader_PostFragment
    ...Moderation_PostFragment
    ...PinnedHeader_PostFragment
    ...Comments_PostFragment
    ...CreateComment_PostFragment
    ...useCommentActions_PostFragment
  }
  ${PostHeader.postFragment}
  ${useModeration.postFragment}
  ${Tags.postFragment}
  ${LikeCommentFooter.postFragment}
  ${ModerationHeader.postFragment}
  ${Moderation.postFragment}
  ${PinnedHeader.postFragment}
  ${Comments.postFragment}
  ${CreateComment_postFragment}
  ${useCommentActions.postFragment}
`;
